import { Link } from '@tanstack/react-router'
import { useId, useState, type SyntheticEvent } from 'react'
import { useAuthStore } from '../auth/AuthProvider'
import { isAuthError } from '../auth/client'
import { AuthBanner, AuthCard, AuthTitle } from './AuthCard'

export const FORGOT_PASSWORD_COPY = {
  title: 'Forgot password',
  intro: "Enter your email and we'll send you a link to set a new password.",
  /** UserFlows A-02: the same answer whether or not the account exists (Security.md §10.2). */
  sent: "If an account exists, we've sent a reset link",
  failed: "Couldn't send the link. Check your connection and try again.",
} as const

/**
 * Codes that would reveal whether an account exists: answered with the same "sent" message (enumeration protection,
 * Security.md §10.2). Anything else (network, throttling) can't be told apart from success by an attacker either,
 * but the user should retry, so it gets an error line.
 */
const SILENT_CODES = new Set(['auth/user-not-found', 'auth/invalid-email', 'auth/missing-email', 'auth/user-disabled'])

/** `continueUrl` for the reset email: this app's A-01 with "Password updated" (Frontend §5.2). */
export const passwordUpdatedUrl = (origin: string = window.location.origin) =>
  `${origin}/sign-in?reason=password-updated`

/** A-02 Forgot password (UserFlows A-02, Frontend §5.2). */
export function ForgotPasswordPage({ siteName }: { siteName: string }) {
  const store = useAuthStore()
  const id = useId()
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'form' | 'sending' | 'sent' | 'failed'>('form')

  async function submit(e: SyntheticEvent) {
    e.preventDefault()
    setState('sending')
    try {
      await store.sendPasswordReset(email.trim(), passwordUpdatedUrl())
      setState('sent')
    } catch (err) {
      setState(isAuthError(err) && SILENT_CODES.has(err.code) ? 'sent' : 'failed')
    }
  }

  return (
    <AuthCard siteName={siteName}>
      <div>
        <AuthTitle>{FORGOT_PASSWORD_COPY.title}</AuthTitle>
        {state !== 'sent' && <p className="muted">{FORGOT_PASSWORD_COPY.intro}</p>}
      </div>
      {state === 'sent' && <AuthBanner>{FORGOT_PASSWORD_COPY.sent}</AuthBanner>}
      {state === 'failed' && <AuthBanner kind="error">{FORGOT_PASSWORD_COPY.failed}</AuthBanner>}
      {state !== 'sent' && (
        <form onSubmit={(e) => void submit(e)} noValidate>
          <div className="field">
            <label className="label" htmlFor={`${id}-email`}>
              Email
            </label>
            <input
              id={`${id}-email`}
              className="input"
              type="email"
              autoComplete="username"
              required
              aria-required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={state === 'sending' || email.trim() === ''}
          >
            {state === 'sending' ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}
      <Link to="/sign-in" className="auth-link">
        Back to sign in
      </Link>
    </AuthCard>
  )
}
